/**
 * The few WebGL helpers the map's own layers share (`cell-columns.ts`, `swarm-pulse.ts`).
 */

import { MercatorCoordinate } from 'maplibre-gl';

export type Point = { x: number; y: number };

/** A position in MapLibre's mercator units, and how long a metre is there. */
export function mercator({ lat, lon }: { lat: number; lon: number }): Point & { metre: number } {
  const point = MercatorCoordinate.fromLngLat({ lng: lon, lat });
  return { x: point.x, y: point.y, metre: point.meterInMercatorCoordinateUnits() };
}

/**
 * The map's matrix with an anchor folded in, in double precision before it becomes a float32
 * uniform.
 *
 * A float32 in mercator units places a point to a few pixels at street zoom, enough for the
 * geometry to tremble while the map moves. Vertices stored relative to an anchor near the data
 * are small numbers and precise, and the anchor itself only ever exists in doubles. Column-major
 * like MapLibre's matrices, so the translation is the last column.
 */
export function anchored(matrix: ArrayLike<number>, anchor: Point): Float32Array {
  const m = Array.from(matrix);
  const at = (index: number) => m[index] ?? 0;
  for (let row = 0; row < 4; row += 1) {
    m[12 + row] = at(row) * anchor.x + at(4 + row) * anchor.y + at(12 + row);
  }
  return new Float32Array(m);
}

/** A GLSL float literal. `1` is an int in GLSL ES 3.00, and the two do not mix. */
export const glsl = (value: number) => (Number.isInteger(value) ? value.toFixed(1) : String(value));

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (shader === null) throw new Error('no shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS) !== true) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(log ?? 'shader did not compile');
  }
  return shader;
}

/** Compiled and linked, or a throw carrying the driver's own message. */
export function buildProgram(
  gl: WebGL2RenderingContext,
  vertex: string,
  fragment: string,
): WebGLProgram {
  const program = gl.createProgram();
  if (program === null) throw new Error('no program');
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, vertex));
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, fragment));
  gl.linkProgram(program);
  if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
    throw new Error(gl.getProgramInfoLog(program) ?? 'program did not link');
  }
  return program;
}

/**
 * A vertex array over one interleaved buffer of floats.
 *
 * `layout` is `[location, components, offset]`, the offset counted in floats, and `stride` is
 * floats per vertex.
 */
export function vertexArray(
  gl: WebGL2RenderingContext,
  buffer: WebGLBuffer,
  layout: readonly (readonly [number, number, number])[],
  stride: number,
): WebGLVertexArrayObject {
  const vao = gl.createVertexArray();
  if (vao === null) throw new Error('no vertex array');
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  for (const [location, size, offset] of layout) {
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride * 4, offset * 4);
  }
  gl.bindVertexArray(null);
  return vao;
}
